import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../hooks/reduxHooks";
import dayjs from "dayjs";

const formatLabel = (value) =>
  String(value || "Unknown")
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const toNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const normalizeCtaTypeRows = (raw) => {
  const byType = {};

  const upsertType = (typeKey) => {
    const prettyType = formatLabel(typeKey);
    if (!byType[prettyType]) {
      byType[prettyType] = {
        open: 0,
        resolved: 0,
        other: 0,
        total: 0,
        originalType: String(typeKey || "unknown"),
      };
    }
    return prettyType;
  };

  const addCounts = (typeKey, openCount, resolvedCount, otherCount) => {
    const key = upsertType(typeKey);
    byType[key].open += toNumber(openCount);
    byType[key].resolved += toNumber(resolvedCount);
    byType[key].other += toNumber(otherCount);
    byType[key].total = byType[key].open + byType[key].resolved + byType[key].other;
  };

  const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];

  if (!list.length && raw && typeof raw === "object" && !Array.isArray(raw)) {
    Object.entries(raw).forEach(([type, value]) => {
      if (!value || typeof value !== "object") return;
      addCounts(
        type,
        value.open_count ?? value.open,
        value.resolved_count ?? value.resolved,
        value.other_count ?? value.other
      );
    });
  }

  list.forEach((item) => {
    if (!item) return;

    const type = item.type || item.cta_type || item.name || item._id || "unknown";

    if (
      item.open_count !== undefined ||
      item.resolved_count !== undefined ||
      item.other_count !== undefined ||
      item.open !== undefined ||
      item.resolved !== undefined ||
      item.other !== undefined
    ) {
      addCounts(
        type,
        item.open_count ?? item.open,
        item.resolved_count ?? item.resolved,
        item.other_count ?? item.other
      );
      return;
    }

    const rawCount = item.count ?? item.value ?? item.total_count ?? item.total ?? 0;
    const status = String(item.status || "").toLowerCase();

    if (status === "open") {
      addCounts(type, rawCount, 0, 0);
    } else if (status === "resolved") {
      addCounts(type, 0, rawCount, 0);
    } else {
      addCounts(type, 0, 0, rawCount);
    }
  });

  return Object.entries(byType)
    .map(([type, counts]) => ({ type, ...counts }))
    .sort((a, b) => b.total - a.total);
};

export default function HumanInterventionTrend({ data }) {
  const chartRef = useRef(null);
  const navigate = useNavigate();
  const { dateRangeValue } = useAppSelector((state) => state.dateRange);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);
    const rows = normalizeCtaTypeRows(data);
    const startDateStr = dayjs(dateRangeValue.start_date)
      .hour(0)
      .minute(0)
      .second(0)
      .format("YYYY-MM-DD HH:mm:ss");
    const endDateStr = dayjs(dateRangeValue.end_date)
      .hour(23)
      .minute(59)
      .second(59)
      .format("YYYY-MM-DD HH:mm:ss");

    if (!rows.length) {
      const title = {
        text: "Ticket Types",
        left: "center",
        textStyle: {
          fontSize: 16,
          fontWeight: "600",
          color: "#0D47A1",
          fontFamily: "'Inter', sans-serif",
        },
      };

      chart.setOption({
        title,
        tooltip: { show: false },
        grid: {
          left: "3%",
          right: "3%",
          top: "22%",
          bottom: "14%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: [""],
          axisLabel: {
            color: "#64748B",
            fontSize: 11,
            interval: 0,
          },
          axisLine: { lineStyle: { color: "#E2E8F0" } },
          axisTick: { alignWithLabel: true },
        },
        yAxis: {
          type: "value",
          name: "Count",
          nameTextStyle: {
            color: "#64748B",
            padding: [0, 0, 0, 25],
          },
          axisLabel: { color: "#64748B" },
          splitLine: {
            lineStyle: {
              color: "#E2E8F0",
              type: "dashed",
            },
          },
        },
        series: [
          {
            name: "Open",
            type: "bar",
            stack: "total",
            data: [{ value: 0, itemStyle: { color: "#f0f0f0" } }],
            barWidth: "28%",
            silent: true,
          },
          {
            name: "Resolved",
            type: "bar",
            stack: "total",
            data: [{ value: 0, itemStyle: { color: "#f0f0f0" } }],
            barWidth: "28%",
            silent: true,
          },
        ],
      });

      const handleResize = () => chart.resize();
      window.addEventListener("resize", handleResize);
      return () => {
        chart.dispose();
        window.removeEventListener("resize", handleResize);
      };
    }

    const types = rows.map((item) => item.type);
    const openData = rows.map((item) => item.open);
    const resolvedData = rows.map((item) => item.resolved);
    const otherData = rows.map((item) => item.other);
    const rowsByType = rows.reduce((acc, row) => {
      acc[row.type] = row;
      return acc;
    }, {});

    const hasOther = otherData.some((value) => value > 0);

    const series = [
      {
        name: "Open",
        type: "bar",
        stack: "total",
        data: openData,
        barWidth: "28%",
        itemStyle: {
          color: "#3B82F6",
          borderRadius: [4, 4, 0, 0],
        },
      },
      {
        name: "Resolved",
        type: "bar",
        stack: "total",
        data: resolvedData,
        barWidth: "28%",
        itemStyle: {
          color: "#10B981",
          borderRadius: [4, 4, 0, 0],
        },
      },
    ];

    if (hasOther) {
      series.push({
        name: "Other",
        type: "bar",
        stack: "total",
        data: otherData,
        barWidth: "28%",
        itemStyle: {
          color: "#F59E0B",
          borderRadius: [4, 4, 0, 0],
        },
      });
    }

    chart.setOption({
      animation: true,
      animationDuration: 700,
      animationEasing: "cubicOut",
      animationDurationUpdate: 0,
      title: {
        text: "Ticket Types",
        left: "center",
        textStyle: {
          fontSize: 16,
          fontWeight: "600",
          color: "#0D47A1",
          fontFamily: "'Inter', sans-serif",
        },
      },
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          const row = rowsByType[params?.name] || {};
          const activeSeries = String(params?.seriesName || "").toLowerCase();
          const openWeight = activeSeries === "open" ? 700 : 400;
          const resolvedWeight = activeSeries === "resolved" ? 700 : 400;

          let html = `
            <div style="font-weight:600; margin-bottom:6px;">${params?.name || "Unknown"}</div>
            <div style="font-weight:${openWeight};">Open: ${row.open ?? 0}</div>
            <div style="font-weight:${resolvedWeight};">Resolved: ${row.resolved ?? 0}</div>
            <div style="font-weight:600;">Total: ${row.total ?? 0}</div>
          `;

          if (hasOther) {
            const otherWeight = activeSeries === "other" ? 700 : 400;
            html += `<div style="font-weight:${otherWeight};">Other: ${row.other ?? 0}</div>`;
          }

          return html;
        },
      },
      legend: {
        top: 30,
        textStyle: {
          color: "#475569",
          fontSize: 12,
        },
      },
      grid: {
        left: "3%",
        right: "3%",
        top: "22%",
        bottom: "14%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: types,
        axisLabel: {
          color: "#64748B",
          fontSize: 11,
          interval: 0,
          rotate: types.length > 6 ? 25 : 0,
        },
        axisLine: { lineStyle: { color: "#E2E8F0" } },
        axisTick: { alignWithLabel: true },
      },
      yAxis: {
        type: "value",
        name: "Count",
        nameTextStyle: {
          color: "#64748B",
          padding: [0, 0, 0, 25],
        },
        axisLabel: { color: "#64748B" },
        splitLine: {
          lineStyle: {
            color: "#E2E8F0",
            type: "dashed",
          },
        },
      },
      series,
    });

    chart.off("click");
    chart.on("click", (params) => {
      const clickedType = params?.name;
      const clickedSeries = String(params?.seriesName || "").toLowerCase();
      const typeRow = rows.find((row) => row.type === clickedType);

      if (!typeRow?.originalType) return;
      if (clickedSeries !== "open" && clickedSeries !== "resolved") return;

      const query = new URLSearchParams({
        type: typeRow.originalType,
        status: clickedSeries,
        start_date: startDateStr,
        end_date: endDateStr,
      });

      navigate(`/cta?${query.toString()}`);
    });

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      chart.dispose();
      window.removeEventListener("resize", handleResize);
    };
  }, [data, dateRangeValue.start_date, dateRangeValue.end_date, navigate]);

  return (
    <div
      style={{
        width: "100%",
        background: "#fff",
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
        padding: "16px",
        marginTop: "24px",
      }}
    >
      <div ref={chartRef} style={{ width: "100%", height: "400px" }} />
    </div>
  );
}
